import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { billingService, } from '../services/billingService';
import { useAuth } from './AuthContext';
import { useEventSubscribe } from '../events';
import { unlimitedPlanLabel } from '../utils/entitlements';
const BillingContext = createContext(undefined);
export const BillingProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [planCatalog, setPlanCatalog] = useState(null);
    const [entitlements, setEntitlements] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const refresh = useCallback(async () => {
        if (!isAuthenticated) {
            setPlanCatalog(null);
            setEntitlements(null);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            // Fetched together: the offer surface needs the price AND the project's
            // demo state, because the demo window decides which price applies.
            const [catalog, ents] = await Promise.all([
                billingService.getPlanCatalog(),
                billingService.getMyEntitlements(),
            ]);
            setPlanCatalog(catalog);
            setEntitlements(ents);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load billing state');
        }
        finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);
    useEffect(() => {
        if (isAuthenticated) {
            refresh();
        }
        else {
            setPlanCatalog(null);
            setEntitlements(null);
            setError(null);
        }
    }, [isAuthenticated, refresh]);
    useEventSubscribe('billing:statusRefresh', () => {
        refresh();
    }, [refresh]);
    const getProjectEntitlement = useCallback((projectId) => entitlements === null || entitlements === void 0 ? void 0 : entitlements.projects.find(p => p.projectId === projectId), [entitlements]);
    const requireAuth = useCallback((action) => {
        if (!isAuthenticated) {
            const message = `You must be logged in to ${action}`;
            setError(message);
            throw new Error(message);
        }
    }, [isAuthenticated]);
    const initiateCheckout = useCallback(async (sku, options = {}) => {
        requireAuth('start checkout');
        setIsLoading(true);
        setError(null);
        try {
            return await billingService.initiateCheckout(sku, options);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to start checkout');
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [requireAuth]);
    const getCheckoutStatus = useCallback(async (sessionId) => {
        requireAuth('check a payment');
        return billingService.getCheckoutStatus(sessionId);
    }, [requireAuth]);
    const setCustomerIntent = useCallback(async (projectId, intent) => {
        requireAuth('record your answer');
        await billingService.setCustomerIntent(projectId, intent);
        // The answer lives on the project's demo window, which the offer surface
        // reads back through entitlements.
        await refresh();
    }, [requireAuth, refresh]);
    const openBillingPortal = useCallback(async (returnUrl) => {
        requireAuth('open the billing portal');
        return billingService.createBillingPortalSession(returnUrl);
    }, [requireAuth]);
    const clearError = useCallback(() => setError(null), []);
    const unlimitedPlanName = useMemo(() => unlimitedPlanLabel(entitlements, planCatalog), [entitlements, planCatalog]);
    const contextValue = useMemo(() => ({
        planCatalog,
        entitlements,
        unlimitedPlanName,
        isLoading,
        error,
        getProjectEntitlement,
        refresh,
        initiateCheckout,
        getCheckoutStatus,
        setCustomerIntent,
        openBillingPortal,
        clearError,
    }), [
        planCatalog,
        entitlements,
        unlimitedPlanName,
        isLoading,
        error,
        getProjectEntitlement,
        refresh,
        initiateCheckout,
        getCheckoutStatus,
        setCustomerIntent,
        openBillingPortal,
        clearError,
    ]);
    return (_jsx(BillingContext.Provider, { value: contextValue, children: children }));
};
export const useBilling = () => {
    const context = useContext(BillingContext);
    if (context === undefined) {
        throw new Error('useBilling must be used within a BillingProvider');
    }
    return context;
};
